// Utilitário para montar o inventário geoespacial (CAR/SIGEF)
// Focado em:
// - Resumo consolidado de áreas registradas (CAR, SIGEF, CAFIR, CCIR)
// - Inventário por código CAR com métricas por alias ambiental (APP, Reserva Legal, etc.)
// - Inventário básico por código SIGEF (sem temas ambientais por enquanto)

import { area as turfArea, union as turfUnion } from '@turf/turf';
import GeometryService from '../../services/GeometryService';
import { getAliasFromNomeTema } from '../../components/map/LayerAliasUtils.jsx';
import { getAreasRegistradasDashboardData } from './areasRegistradas';

const AREA_IMOVEL_ALIAS = 'ÁREA DO IMÓVEL';

const ENV_ALIAS_KEYS = [
  'APP',
  'RESERVA LEGAL',
  'USO RESTRITO',
  'ÁREA CONSOLIDADA',
  'VEGETAÇÃO NATIVA',
  'ÁREA NÃO CLASSIFICADA',
  'ÁREA DE SERVIDÃO',
  'ÁREA DE POUSIO',
];

const ENV_ALIAS_SET = new Set(ENV_ALIAS_KEYS);

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const safeTurfAreaHaFromUnion = (features) => {
  if (!Array.isArray(features) || features.length === 0) return null;

  let unionGeom = null;

  try {
    features.forEach((feat) => {
      if (!feat) return;

      const feature =
        feat.type === 'Feature'
          ? feat
          : {
              type: 'Feature',
              geometry: feat.geometry || feat,
              properties: {},
            };

      if (!feature.geometry) return;

      if (!unionGeom) {
        unionGeom = feature;
      } else {
        const next = turfUnion(unionGeom, feature);
        if (next) {
          unionGeom = next;
        }
      }
    });
  } catch (_e) {
    // Fallback: soma simples das áreas em caso de erro na união
    let sumM2 = 0;

    features.forEach((feat) => {
      try {
        const feature =
          feat.type === 'Feature'
            ? feat
            : {
                type: 'Feature',
                geometry: feat.geometry || feat,
                properties: {},
              };

        const areaM2 = turfArea(feature);
        if (Number.isFinite(areaM2) && areaM2 > 0) {
          sumM2 += areaM2;
        }
      } catch (_err) {
        // ignora geometrias inválidas individualmente
      }
    });

    return sumM2 > 0 ? sumM2 / 10000 : null;
  }

  if (!unionGeom) return null;

  const areaM2 = turfArea(unionGeom);
  if (!Number.isFinite(areaM2) || areaM2 <= 0) return null;

  return areaM2 / 10000;
};

const computeDeltaMetrics = (declaredHa, calculatedHa) => {
  const declared = toNumberOrNull(declaredHa);
  const calculated = toNumberOrNull(calculatedHa);

  if (calculated === null) {
    return {
      declaredHa: declared,
      calculatedHa: null,
      deltaHa: null,
      deltaPct: null,
    };
  }

  if (declared === null || declared === 0) {
    return {
      declaredHa: declared,
      calculatedHa: calculated,
      deltaHa: null,
      deltaPct: null,
    };
  }

  const deltaHa = calculated - declared;
  const deltaPct = (deltaHa / declared) * 100;

  return {
    declaredHa: declared,
    calculatedHa: calculated,
    deltaHa,
    deltaPct,
  };
};

const buildDeclaredAreasFromCarDetails = (details) => {
  const result = {};

  if (!details || typeof details !== 'object') {
    return result;
  }

  const getNum = (field) => toNumberOrNull(details[field]);

  // Área do Imóvel (alias específico, tratado também diretamente no item)
  const areaImovel = getNum('area_da_propriedade');
  if (areaImovel !== null) {
    result[AREA_IMOVEL_ALIAS] = areaImovel;
  }

  // APP
  const appArea = getNum('area_de_preservacao_permanente');
  if (appArea !== null) {
    result['APP'] = appArea;
  }

  // Uso Restrito
  const usoRestrito = getNum('area_de_uso_restrito');
  if (usoRestrito !== null) {
    result['USO RESTRITO'] = usoRestrito;
  }

  // Área Consolidada
  const areaConsolidada = getNum('area_rural_consolidada');
  if (areaConsolidada !== null) {
    result['ÁREA CONSOLIDADA'] = areaConsolidada;
  }

  // Vegetação Nativa
  const vegNativa = getNum('area_remanescente_de_vegetacao_nativa');
  if (vegNativa !== null) {
    result['VEGETAÇÃO NATIVA'] = vegNativa;
  }

  // Área Não Classificada
  const naoClassificada = getNum('area_nao_classificada');
  if (naoClassificada !== null) {
    result['ÁREA NÃO CLASSIFICADA'] = naoClassificada;
  }

  // Área de Servidão Administrativa
  const servidao = getNum('area_de_servidao_administrativa');
  if (servidao !== null) {
    result['ÁREA DE SERVIDÃO'] = servidao;
  }

  // Área de Pousio
  const pousio = getNum('area_de_pousio');
  if (pousio !== null) {
    result['ÁREA DE POUSIO'] = pousio;
  }

  // Reserva Legal: soma dos campos de RL que estiverem > 0
  const rlProposta = getNum('area_de_reserva_legal_proposta');
  const rlAverbada = getNum('area_de_reserva_legal_averbada');
  const rlAprovNaoAverb = getNum('area_de_reserva_legal_aprovada_e_nao_averbada');

  let rlSum = 0;
  let hasRl = false;

  [rlProposta, rlAverbada, rlAprovNaoAverb].forEach((value) => {
    if (value !== null && value > 0) {
      rlSum += value;
      hasRl = true;
    }
  });

  if (hasRl) {
    result['RESERVA LEGAL'] = rlSum;
  }

  return result;
};

export function buildGeoespacialInventory(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    return {
      summaryAreas: null,
      carItems: [],
      sigefItems: [],
    };
  }

  const properties =
    snapshot.properties && typeof snapshot.properties === 'object' ? snapshot.properties : {};

  // Reaproveita o cálculo consolidado de áreas registradas do dashboard
  const summaryAreas = getAreasRegistradasDashboardData({ properties }, null);

  // Inventário por código CAR
  const carRaw = properties.car;
  const carArray = Array.isArray(carRaw) ? carRaw : carRaw ? [carRaw] : [];

  const carItems = carArray.map((carItem) => {
    const details = carItem && typeof carItem === 'object' ? carItem.details || {} : {};
    const codImovel = carItem && typeof carItem === 'object' ? carItem.cod_imovel || '' : '';
    const areaDeclarada = toNumberOrNull(details.area_da_propriedade);
    const declaredByAlias = buildDeclaredAreasFromCarDetails(details);

    return {
      tipo: 'CAR',
      codigo: codImovel,
      areaImovelDeclaradaHa: areaDeclarada,
      areaImovelCalculadaHa: null,
      deltaAreaImovelHa: null,
      deltaAreaImovelPct: null,
      areaImovelFeatures: [],
      areaImovelLayer: null,
      layers: {}, // alias ambiental -> métricas + features
      declaredByAlias,
    };
  });

  // Inventário por código SIGEF (será enriquecido com áreas e geometrias abaixo)
  const sigefRaw = properties.sigef;
  const sigefArray = Array.isArray(sigefRaw) ? sigefRaw : sigefRaw ? [sigefRaw] : [];

  const sigefItems = sigefArray.map((sigefItem) => {
    const codigoImo = sigefItem && typeof sigefItem === 'object' ? sigefItem.codigo_imo || '' : '';
    const parcels = sigefItem && Array.isArray(sigefItem.parcels) ? sigefItem.parcels : [];

    return {
      tipo: 'SIGEF',
      codigo: codigoImo,
      areaImovelDeclaradaHa: null,
      areaImovelCalculadaHa: null,
      deltaAreaImovelHa: null,
      deltaAreaImovelPct: null,
      // Métricas específicas SIGEF
      areaTotalSigefHa: null,
      baseFeatures: [],
      parcels: [], // { parcela, features, areaHa }
      parcelsCount: parcels.length,
    };
  });

  const carByCode = new Map();
  if (carItems.length > 0) {
    carItems.forEach((item) => {
      const code = item.codigo || '';
      if (!carByCode.has(code)) {
        carByCode.set(code, item);
      }
    });
  }

  const sigefByCode = new Map();
  if (sigefItems.length > 0) {
    sigefItems.forEach((item) => {
      const code = item.codigo || '';
      if (!sigefByCode.has(code)) {
        sigefByCode.set(code, item);
      }
    });
  }

  // eslint-disable-next-line no-useless-assignment
  let geometryResult = null;
  try {
    const propertyData = {
      properties,
      _metadata: snapshot._metadata || {},
    };
    geometryResult = GeometryService.processPropertyGeometries(propertyData);
  } catch (_e) {
    // Falha no processamento de geometrias não deve quebrar o relatório
    geometryResult = null;
  }

  // 1) Área do Imóvel (geometria) por código CAR
  if (carItems.length > 0 && geometryResult && Array.isArray(geometryResult.carGeometries)) {
    const areaImovelFeaturesByCode = new Map();

    geometryResult.carGeometries.forEach((entry) => {
      if (!entry || !entry.geojson) return;

      const code =
        entry.metadata && entry.metadata.codigo_imovel ? entry.metadata.codigo_imovel : '';
      if (!code) return;

      const feature = {
        type: 'Feature',
        geometry: entry.geojson,
        properties: { ...(entry.metadata || {}) },
      };

      const arr = areaImovelFeaturesByCode.get(code) || [];
      arr.push(feature);
      areaImovelFeaturesByCode.set(code, arr);
    });

    areaImovelFeaturesByCode.forEach((features, code) => {
      const item = carByCode.get(code);
      if (!item) return;

      const calculatedHa = safeTurfAreaHaFromUnion(features);
      const metrics = computeDeltaMetrics(item.areaImovelDeclaradaHa, calculatedHa);

      item.areaImovelCalculadaHa = metrics.calculatedHa;
      item.deltaAreaImovelHa = metrics.deltaHa;
      item.deltaAreaImovelPct = metrics.deltaPct;
      item.areaImovelFeatures = features;
      item.areaImovelLayer = {
        alias: AREA_IMOVEL_ALIAS,
        declaredHa: metrics.declaredHa,
        calculatedHa: metrics.calculatedHa,
        deltaHa: metrics.deltaHa,
        deltaPct: metrics.deltaPct,
        features,
      };
    });
  }

  // 2) Camadas ambientais (APP, Reserva Legal, etc.) por alias e código CAR
  if (carItems.length > 0 && geometryResult && geometryResult.themeGeometries) {
    const themeGeometries = geometryResult.themeGeometries;

    Object.entries(themeGeometries).forEach(([nomeTema, features]) => {
      if (!Array.isArray(features) || features.length === 0) return;

      const alias = getAliasFromNomeTema(nomeTema);
      if (!ENV_ALIAS_SET.has(alias)) {
        return;
      }

      features.forEach((feat) => {
        if (!feat) return;

        const code =
          feat.properties && typeof feat.properties.codigo_imovel === 'string'
            ? feat.properties.codigo_imovel
            : '';
        if (!code) return;

        const carItem = carByCode.get(code);
        if (!carItem) return;

        if (!carItem.layers) {
          carItem.layers = {};
        }

        let layer = carItem.layers[alias];
        if (!layer) {
          layer = {
            alias,
            features: [],
            declaredHa: 0,
            calculatedHa: null,
            deltaHa: null,
            deltaPct: null,
          };
          carItem.layers[alias] = layer;
        }

        layer.features.push(feat);

        const declared = toNumberOrNull(
          feat.properties && feat.properties.area !== undefined ? feat.properties.area : null,
        );
        if (declared !== null && declared > 0) {
          layer.declaredHa += declared;
        }
      });
    });

    carItems.forEach((item) => {
      if (!item.layers) return;

      const declaredMap = item.declaredByAlias || {};

      // Garante layers mesmo quando só há área declarada em details (sem geometria)
      ENV_ALIAS_KEYS.forEach((alias) => {
        const declaredFromDetails = toNumberOrNull(declaredMap[alias]);
        if (declaredFromDetails === null) return;

        if (!item.layers[alias]) {
          item.layers[alias] = {
            alias,
            features: [],
            declaredHa: declaredFromDetails,
            calculatedHa: null,
            deltaHa: null,
            deltaPct: null,
          };
        }
      });

      Object.values(item.layers).forEach((layer) => {
        const calculatedHa = safeTurfAreaHaFromUnion(layer.features);

        const declaredFromDetails = toNumberOrNull(declaredMap[layer.alias]);
        const declaredBase =
          declaredFromDetails !== null ? declaredFromDetails : toNumberOrNull(layer.declaredHa);

        const metrics = computeDeltaMetrics(declaredBase, calculatedHa);

        layer.declaredHa = metrics.declaredHa;
        layer.calculatedHa = metrics.calculatedHa;
        layer.deltaHa = metrics.deltaHa;
        layer.deltaPct = metrics.deltaPct;
      });
    });
  }

  // 3) SIGEF: união de parcelas por código_imo, área total e área por parcela
  if (sigefItems.length > 0 && geometryResult && Array.isArray(geometryResult.sigefGeometries)) {
    const aggByCodigo = new Map();

    geometryResult.sigefGeometries.forEach((entry) => {
      if (!entry || !entry.geojson) return;

      const code = entry.metadata && entry.metadata.codigo_imo ? entry.metadata.codigo_imo : '';
      if (!code) return;

      const parcelaCo =
        entry.metadata && typeof entry.metadata.parcela_co === 'string'
          ? entry.metadata.parcela_co
          : '';

      const feature = {
        type: 'Feature',
        geometry: entry.geojson,
        properties: { ...(entry.metadata || {}) },
      };

      let agg = aggByCodigo.get(code);
      if (!agg) {
        agg = {
          allFeatures: [],
          parcelsMap: new Map(),
        };
        aggByCodigo.set(code, agg);
      }

      agg.allFeatures.push(feature);

      if (parcelaCo) {
        const arr = agg.parcelsMap.get(parcelaCo) || [];
        arr.push(feature);
        agg.parcelsMap.set(parcelaCo, arr);
      }
    });

    aggByCodigo.forEach((agg, code) => {
      const sigefItem = sigefByCode.get(code);
      if (!sigefItem) return;

      // União das parcelas para geometria base (apenas para visualização)
      let unionGeom = null;
      try {
        agg.allFeatures.forEach((feat) => {
          if (!feat) return;

          const feature =
            feat.type === 'Feature'
              ? feat
              : {
                  type: 'Feature',
                  geometry: feat.geometry || feat,
                  properties: {},
                };

          if (!feature.geometry) return;

          if (!unionGeom) {
            unionGeom = feature;
          } else {
            const next = turfUnion(unionGeom, feature);
            if (next) {
              unionGeom = next;
            }
          }
        });
      } catch (_e) {
        unionGeom = null;
      }

      const areaTotalHa = safeTurfAreaHaFromUnion(agg.allFeatures);

      sigefItem.areaTotalSigefHa = areaTotalHa;
      sigefItem.baseFeatures = unionGeom ? [unionGeom] : agg.allFeatures.slice();

      const parcelsList = [];
      agg.parcelsMap.forEach((features, parcelaCo) => {
        if (!parcelaCo) return;
        const areaHa = safeTurfAreaHaFromUnion(features);
        parcelsList.push({ parcela: parcelaCo, features, areaHa });
      });

      sigefItem.parcels = parcelsList;
      sigefItem.parcelsCount = parcelsList.length;
    });
  }

  return {
    summaryAreas,
    carItems,
    sigefItems,
  };
}
